import { Link, useLocation, useParams } from 'react-router'

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { useBot } from '@/contexts/bot'
import useTranslator from '@/hooks/translation'
import { useQueryParams } from '@/hooks/use-query-params'
import { toQueryString } from '@/utils/to-query-string'

export const BotBreadcrumbs = () => {
  const location = useLocation()
  const params = useParams()
  const { getTranslation } = useTranslator()

  const { getQueryParams } = useQueryParams()

  const botId = params.botId!
  const pathname = location.pathname

  const { bot } = useBot()
  const { projectId, companyId } = getQueryParams()

  const getHomePath = () => {
    if (projectId) {
      const queryString = toQueryString({
        companyId,
      })

      return `/projects/${projectId}/bots?${queryString}`
    }

    return '/'
  }

  const getHomeLabel = () => {
    if (projectId) {
      return getTranslation('common.bots')
    }

    return getTranslation('common.home')
  }

  const getPageLabel = (): string => {
    if (pathname.includes('/builder')) {
      return getTranslation('bots.sidebar.builder')
    }

    if (pathname.includes('/agentik')) {
      if (pathname.includes('/agents')) {
        if (pathname.includes('/edit')) {
          return getTranslation('bubble.agents.edit')
        }

        if (pathname.includes('/create')) {
          return getTranslation('bubble.agents.create')
        }

        return getTranslation('bubble.agents.list.title')
      }

      if (pathname.includes('/tools')) {
        if (pathname.includes('/edit')) {
          return getTranslation('bubble.tasks.edit.label')
        }

        if (pathname.includes('/create')) {
          return getTranslation('bubble.tasks.create.label')
        }

        return getTranslation('bubble.tasks.list.title')
      }

      return getTranslation('bots.sidebar.bubble.bubble')
    }

    if (pathname.includes('/dashboards')) {
      return getTranslation('bots.sidebar.dashboard')
    }

    if (pathname.includes('/knowledge-base')) {
      return getTranslation('bots.sidebar.knowledgeBase')
    }

    if (pathname.includes('/members')) {
      if (pathname.includes('/add')) {
        return getTranslation('members.newMember')
      }

      if (pathname.includes('/edit')) {
        return getTranslation('members.edit')
      }

      return getTranslation('members.text')
    }

    if (pathname.includes('/campaign-module')) {
      if (pathname.includes('/templates')) {
        return getTranslation('campaignModule.templates.title')
      }

      if (pathname.includes('/actionings')) {
        return getTranslation('campaignModule.actionings.metadata')
      }

      if (pathname.includes('/campaigns')) {
        return getTranslation('campaignModule.campaigns.metadata')
      }

      return getTranslation('bots.sidebar.campaigns.campaignModule')
    }

    if (pathname.includes('/campaign/')) {
      if (pathname.includes('/templates')) {
        return getTranslation('campaign.templates.title')
      }

      if (pathname.includes('/dispatches/create')) {
        const isDuplicate = new URLSearchParams(location.search).has(
          'duplicateFrom',
        )

        return getTranslation(
          isDuplicate
            ? 'campaign.dispatches.form.duplicateDispatch'
            : 'campaign.dispatches.form.createDispatch',
        )
      }

      if (
        pathname.includes('/campaign/dispatches/') &&
        pathname.includes('/edit')
      ) {
        return getTranslation('campaign.dispatches.form.editDispatch')
      }

      if (pathname.includes('/dispatches')) {
        return getTranslation('campaign.dispatches.title')
      }

      return getTranslation('bots.sidebar.campaign.title')
    }

    if (pathname.includes('/bot-rules')) {
      return getTranslation('bots.sidebar.followUp')
    }

    if (pathname.includes('/conversation-history')) {
      return getTranslation('bots.sidebar.conversationHistory')
    }

    if (pathname.includes('/n8n')) {
      return getTranslation('bots.sidebar.n8n')
    }

    if (pathname.includes('/settings')) {
      if (pathname.includes('/integrations')) {
        return getTranslation('bots.sidebar.settings.integrations')
      }

      return getTranslation('bots.sidebar.settings.title')
    }

    return ''
  }

  const getCampaignModuleParent = (): string | null => {
    if (!pathname.includes('/campaign-module')) {
      return null
    }

    if (
      pathname.includes('/templates') ||
      pathname.includes('/actionings') ||
      pathname.includes('/campaigns')
    ) {
      return getTranslation('bots.sidebar.campaigns.campaignModule')
    }

    return null
  }

  const getCampaignParent = (): string | null => {
    if (!pathname.includes('/campaign/')) {
      return null
    }

    if (pathname.includes('/templates') || pathname.includes('/dispatches')) {
      return getTranslation('bots.sidebar.campaign.title')
    }

    return null
  }

  const getMembersParent = (): string | null => {
    if (
      pathname.includes('/members/add') ||
      pathname.includes('/members/edit')
    ) {
      return getTranslation('members.text')
    }

    return null
  }

  const getMembersParentPath = () => {
    const queryString = toQueryString({
      companyId,
      projectId,
    })

    return `/bots/${botId}/members?${queryString}`
  }

  const getSettingsParent = (): string | null => {
    if (pathname.includes('integrations')) {
      return getTranslation('bots.sidebar.settings.title')
    }

    return null
  }

  const getBubbleParent = (): string | null => {
    if (
      pathname.includes('/agentik/agents/create') ||
      (pathname.includes('/agentik/agents/') && pathname.includes('/edit'))
    ) {
      return getTranslation('bubble.agents.list.title')
    }

    return null
  }

  const getBubbleParentPath = () => {
    const queryString = toQueryString({
      companyId,
      projectId,
    })

    return `/bots/${botId}/agentik/agents/list?${queryString}`
  }

  const getBubbleToolsParent = (): string | null => {
    if (
      pathname.includes('/agentik/tools/create') ||
      (pathname.includes('/agentik/tools/') && pathname.includes('/edit'))
    ) {
      return getTranslation('bubble.tasks.list.title')
    }

    return null
  }

  const getBubbleToolsParentPath = () => {
    const queryString = toQueryString({
      companyId,
      projectId,
    })

    return `/bots/${botId}/agentik/tools/list?${queryString}`
  }

  const getDispatchesParent = (): string | null => {
    const isOnDispatchForm =
      pathname.includes('/campaign/dispatches/create') ||
      (pathname.includes('/campaign/dispatches/') && pathname.includes('/edit'))

    if (!isOnDispatchForm) {
      return null
    }

    return getTranslation('campaign.dispatches.title')
  }

  const getDispatchesParentPath = () => {
    const queryString = toQueryString({
      companyId,
      projectId,
    })

    return `/bots/${botId}/campaign/dispatches/list?${queryString}`
  }

  const pageLabel = getPageLabel()
  const campaignModuleParent = getCampaignModuleParent()
  const campaignParent = getCampaignParent()
  const dispatchesParent = getDispatchesParent()
  const membersParent = getMembersParent()
  const settingsParent = getSettingsParent()
  const bubbleParent = getBubbleParent()
  const bubbleToolsParent = getBubbleToolsParent()

  if (!pageLabel) return null

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to={getHomePath()}>{getHomeLabel()}</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />

        <BreadcrumbItem>{bot.name}</BreadcrumbItem>
        <BreadcrumbSeparator />

        {campaignModuleParent && (
          <>
            <BreadcrumbItem>{campaignModuleParent}</BreadcrumbItem>
            <BreadcrumbSeparator />
          </>
        )}

        {campaignParent && (
          <>
            <BreadcrumbItem>{campaignParent}</BreadcrumbItem>
            <BreadcrumbSeparator />
          </>
        )}

        {dispatchesParent && (
          <>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={getDispatchesParentPath()}>{dispatchesParent}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
          </>
        )}

        {membersParent && (
          <>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={getMembersParentPath()}>{membersParent}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
          </>
        )}

        {settingsParent && (
          <>
            <BreadcrumbItem>{settingsParent}</BreadcrumbItem>
            <BreadcrumbSeparator />
          </>
        )}

        {bubbleParent && (
          <>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={getBubbleParentPath()}>{bubbleParent}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
          </>
        )}

        {bubbleToolsParent && (
          <>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={getBubbleToolsParentPath()}>{bubbleToolsParent}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
          </>
        )}

        <BreadcrumbItem>
          <BreadcrumbPage>{pageLabel}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  )
}
